import { Done as DoneIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Card,
  CardContent,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSignoff } from "components/AsolviSignoff";
import { AsolviSwitch } from "components/AsolviSwitch";
import BackdropWhite from "components/BackdropWhite";
import { CustomerSatisfactionSurvey } from "components/job/completion/CustomerSatisfactionSurvey";
import { JobVisitSummary } from "components/job/completion/JobVisitSummary";
import { StorableSignoff } from "components/job/completion/StorableSignoff";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import { chooseContact, isEmpty } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { JobVisitType } from "models/JobVisitType";
import { ContactType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { getContacts, selectSelectedJob, setVisitValue } from "store/slices/partner.store";

interface SignoffDialogProps {
  jobVisit: JobVisitType;
  disableCompleteVisitButton: boolean;
  loading: boolean;
  handleSignature: (value: string | undefined) => void;
  handleComplete: () => void;
}

export const CustomerSignoffComponent: FC<SignoffDialogProps> = (props: SignoffDialogProps) => {
  const { jobVisit, handleComplete, handleSignature, disableCompleteVisitButton, loading } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const {
    customerSignerName,
    skipSignature,
    reference,
    signatureCustomer,
    customerSatisfaction,
    serviceReportEmail,
  } = jobVisit;
  const serviceReportEmailEnabled = technicianSettings.sendMailToCustomer ?? false;
  const customerSignerNameRequired = technicianSettings.requireCustomerSignatureName ?? false;
  const customerSatisfactionEnabled = technicianSettings.customerSatisfactionEnabled;
  const storeSignaturesLocally = technicianSettings.storeSignaturesLocal ?? false;

  const [expandedSignature, setExpandedSignature] = useState(true);
  const [contacts, setContacts] = useState<ContactType[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<ContactType[]>([]);

  const dispatchGetContacts = useCallback(() => {
    dispatch(getContacts())
      .then(unwrapResult)
      .then((contacts) => setContacts(contacts.filter((c) => !isEmpty(c.email))));
  }, [dispatch]);

  useEffect(() => {
    if (serviceReportEmailEnabled) {
      dispatchGetContacts();

      const contact = chooseContact(job.preferredContact, job.customer?.contacts);
      var inSelection = selectedContacts.filter((c) => c.email === contact?.email).length > 0;
      if (contact && contact.email && serviceReportEmail?.includes(contact.email) && !inSelection) {
        var contactData: ContactType = {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          mobile: contact.mobileNumber,
          phone: contact.phoneNumber,
          email: contact.email,
          name: contact.name ?? "",
        };
        setSelectedContacts([...selectedContacts, contactData]);
      }
    }
  }, [
    serviceReportEmailEnabled,
    dispatchGetContacts,
    job.preferredContact,
    job.customer?.contacts,
    selectedContacts,
    serviceReportEmail,
  ]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item sx={{ width: "100%" }}>
        <JobVisitSummary jobVisit={jobVisit} />
      </Grid>
      <Grid item sx={{ width: "100%" }}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <StyledTextField
              id="signature-name"
              name="customerSignerName"
              label={intl.formatMessage({ id: "job.customer.name" })}
              value={customerSignerName}
              onChange={({ target: { value } }) => {
                dispatch(setVisitValue({ key: "customerSignerName", value }));
              }}
              error={!customerSignerName && customerSignerNameRequired}
            />
            {!customerSignerName && customerSignerNameRequired && (
              <FormHelperText error>
                {intl.formatMessage({ id: "visit.signoff.customerSignerNameRequired" })}
              </FormHelperText>
            )}
          </Grid>
          {serviceReportEmailEnabled && (
            <Grid
              item
              style={{
                display: "flex",
              }}
            >
              <div
                style={{
                  flex: "1 1 auto",
                }}
              >
                <Autocomplete
                  fullWidth
                  multiple
                  freeSolo
                  options={contacts}
                  getOptionLabel={(option: string | ContactType) => {
                    if (typeof option === "string") return option;
                    return option.name === ""
                      ? `${option.email}`
                      : `${option.email} - ${option.name}`;
                  }}
                  onChange={(_, value) => {
                    var currentContacts = (value as ContactType[]).filter((c) => c.email);
                    var newItem = value[value.length - 1];

                    if (typeof newItem === "string") {
                      if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(newItem)) {
                        var newContact: ContactType = {
                          id: "",
                          email: newItem,
                          name: "",
                        };
                        currentContacts.push(newContact);
                        setSelectedContacts(currentContacts);
                      }
                    } else {
                      setSelectedContacts(currentContacts);
                    }
                    dispatch(
                      setVisitValue({
                        key: "serviceReportEmail",
                        value: currentContacts.map((c) => c.email).join(";"),
                      }),
                    );
                  }}
                  value={selectedContacts}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label={intl.formatMessage({
                        id: "visit.signoff.sendServiceReportTo",
                      })}
                    />
                  )}
                />
              </div>
            </Grid>
          )}

          <Grid item>
            <StyledTextField
              label={intl.formatMessage({ id: "visit.reference" })}
              data-testid="CustomerReference"
              value={reference}
              onChange={(e) => {
                dispatch(
                  setVisitValue({
                    key: "reference",
                    value: e.target.value,
                  }),
                );
              }}
            />
          </Grid>

          <Grid item container justifyContent="space-between" alignItems="center" wrap="nowrap">
            <Grid item xs={10}>
              <FormattedMessage id="visit.signoff.skipSignature" />
            </Grid>
            <Grid item>
              <AsolviSwitch
                disabled={technicianSettings.requireCustomerSignature}
                checked={skipSignature}
                onChange={({ target: { checked } }) => {
                  dispatch(setVisitValue({ key: "skipSignature", value: checked }));
                }}
                name="skipSignature"
              />
            </Grid>
          </Grid>
          {customerSatisfactionEnabled && !skipSignature && (
            <CustomerSatisfactionSurvey
              customerSatisfaction={customerSatisfaction}
              onChange={(value) => {
                dispatch(
                  setVisitValue({
                    key: "customerSatisfaction",
                    value,
                  }),
                );
              }}
            />
          )}
          <Grid item sx={{ width: "100%" }}>
            <Accordion disabled={skipSignature} expanded={expandedSignature && !skipSignature}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                onClick={() => setExpandedSignature(!expandedSignature)}
              >
                <Typography>
                  <FormattedMessage id="general.signature" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {storeSignaturesLocally ? (
                  <>
                    <Grid item>
                      <FormattedMessage id="visit.signoff.additionalCosts" />
                    </Grid>
                    <StorableSignoff
                      externalId={job.externalId}
                      handleSignature={handleSignature}
                      label={intl.formatMessage({ id: "visit.signoff.customerSignature" })}
                      signatureData={signatureCustomer}
                    />
                  </>
                ) : (
                  <Card elevation={1}>
                    <CardContent>
                      <AsolviSignoff
                        handleSignature={handleSignature}
                        labelText={intl.formatMessage({ id: "visit.signoff.customerSignature" })}
                      />
                    </CardContent>
                  </Card>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Grid>
      <Grid item pb={1.5}>
        <BackdropWhite open={loading}>
          <CircularProgress color="inherit" />
        </BackdropWhite>

        <PrimaryButton
          key={`${disableCompleteVisitButton}`}
          startIcon={<DoneIcon />}
          variant="contained"
          onClick={handleComplete}
          fullWidth
          disabled={disableCompleteVisitButton}
        >
          <FormattedMessage id="visit.complete" />
        </PrimaryButton>
      </Grid>
    </Grid>
  );
};
